import { timing } from "../config";
/**
 * The words of the Telegram grid on Notifications: a chat's name and kind,
 * who connected it, its last delivery or why it stopped, a Send test's answer
 * per chat, and an event's deliveries.
 */

import type { ChatTest, NotificationEvent, StopReason, TelegramChat } from "./api";
import { utcClock, utcDayTime } from "./time";

const KIND: Record<string, string> = { private: "Private chat", group: "Group", supergroup: "Group", channel: "Channel" };

/** The group's title or the person's name; a carried-over chat not named yet, the id saved before. */
export function chatName(chat: TelegramChat): string {
  return chat.title ?? chat.savedAs ?? "A chat";
}

export function chatKind(chat: TelegramChat): string {
  return chat.type === null ? "Chat" : (KIND[chat.type] ?? "Chat");
}

/** The icon a row draws: a person, a group, or a channel. */
export function chatIcon(chat: TelegramChat): "private" | "group" | "channel" {
  return chat.type === "private" ? "private" : chat.type === "channel" ? "channel" : chat.type === null && chat.savedAs?.startsWith("@") === true ? "channel" : "group";
}

const STOPPED: Record<StopReason, string> = {
  "bot-blocked": "blocked the bot",
  "bot-removed": "the bot was removed",
  command: "/stop was sent",
  "wallet-removed": "its wallet was removed",
  "pass-burned": "its operator pass was revoked",
};

/** Why a chat stopped, as the rest of a sentence: "blocked the bot". */
export function stopWords(reason: string): string {
  return STOPPED[reason as StopReason] ?? "it stopped receiving";
}

const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A delivery error code (`http-429`, `TimeoutError`) in words. */
export function errorWords(error: string): string {
  const status = /^http-(\d{3})$/.exec(error);
  if (status !== null) return `Telegram answered ${status[1]!}`;
  if (error === "TimeoutError" || error === "AbortError") return "Telegram did not answer in time";
  return "Telegram could not be reached";
}

export type Tone = "plain" | "ok" | "red" | "amber" | "grey";

export interface Cell {
  text: string;
  sub: string | null;
  tone: Tone;
}

/** The Last delivery cell: the last message Telegram took, the last it refused when newer, or why the chat stopped. */
export function lastDelivery(chat: TelegramChat): Cell {
  if (chat.stopped !== null) return { text: `Stopped: ${stopWords(chat.stopped.reason)}`, sub: `${utcDayTime(chat.stopped.at)} UTC. Nothing is sent to it.`, tone: "red" };
  const error = chat.lastError;
  if (error !== null && (chat.lastDeliveredAt === null || Date.parse(error.at) > Date.parse(chat.lastDeliveredAt))) {
    return { text: `Not delivered: ${errorWords(error.error)}`, sub: `${utcDayTime(error.at)} UTC. It keeps receiving; the next message tries again.`, tone: "amber" };
  }
  if (chat.lastDeliveredAt !== null) return { text: `Delivered ${utcDayTime(chat.lastDeliveredAt)} UTC`, sub: null, tone: "plain" };
  return { text: "Nothing sent yet", sub: null, tone: "grey" };
}

/** Why Telegram refused a chat for good, by what the chat is. */
function refusedWhy(chat: TelegramChat): string {
  if (chat.type === "private") return "the person blocked the bot";
  if (chat.type === "channel") return "the bot is no longer in this channel";
  if (chat.type === null) return "the bot can no longer post there";
  return "the bot is no longer in this group";
}

/** A Send test's answer in a chat's row; `result` undefined: the chat was not tried. `at`: when the test was answered. */
export function testCell(chat: TelegramChat, result: ChatTest | undefined, at: string): Cell {
  if (result === undefined) {
    return chat.stopped !== null
      ? { text: "Not sent: stopped earlier", sub: `${upperFirst(stopWords(chat.stopped.reason))} on ${utcDayTime(chat.stopped.at)} UTC`, tone: "grey" }
      : { text: "Not sent", sub: null, tone: "grey" };
  }
  if (result.delivered) return { text: `Delivered ${utcClock(at)} UTC`, sub: null, tone: "ok" };
  if (result.stopped) return { text: `Refused: ${refusedWhy(chat)}`, sub: `Stopped ${utcDayTime(at)} UTC. Connect it again to restart it.`, tone: "red" };
  return { text: `Not delivered: ${result.error === null ? "Telegram did not take it" : errorWords(result.error)}`, sub: "It keeps receiving; the next message tries again.", tone: "amber" };
}

const chats = (n: number) => (n === 1 ? "1 chat" : `${n} chats`);

/** The line over the grid after Send test. */
export function testSummary(tested: readonly TelegramChat[], results: readonly ChatTest[], at: string): { text: string; ok: boolean } {
  const delivered = results.filter((r) => r.delivered).length;
  const name = (r: ChatTest) => {
    const chat = tested.find((c) => c.id === r.chat);
    return chat === undefined ? "a chat" : chatName(chat);
  };
  const stopped = results.filter((r) => r.stopped).map(name);
  const failed = results.filter((r) => !r.delivered && !r.stopped).map(name);
  const every = results.length === 1 ? "the chat" : results.length === 2 ? "both chats" : `all ${chats(results.length)}`;
  const head = delivered === results.length ? `Test sent at ${utcClock(at)} UTC: delivered to ${every}.` : `Test sent at ${utcClock(at)} UTC: delivered to ${delivered} of ${chats(results.length)}.`;
  const parts = [head];
  if (stopped.length > 0) parts.push(`Telegram refused it in ${join(stopped)}, so ${stopped.length === 1 ? "that chat" : "those chats"} stopped.`);
  if (failed.length > 0) parts.push(`${join(failed)} did not get it; ${failed.length === 1 ? "it keeps" : "they keep"} receiving.`);
  return { text: parts.join(" "), ok: delivered === results.length };
}

const join = (items: readonly string[]) => (items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]!}`);

/** An event's Telegram column: how many chats got it. */
export function sentWords(event: NotificationEvent): string {
  const counts = event.chats ?? null;
  if (counts === null) return event.sent ? "Sent" : "Not sent";
  if (counts.delivered === 0) return "Not sent";
  return counts.delivered === counts.sent ? `Sent to ${chats(counts.delivered)}` : `Sent to ${counts.delivered} of ${chats(counts.sent)}`;
}

/** The chats that receive now. */
export function receiving(list: readonly TelegramChat[]): TelegramChat[] {
  return list.filter((c) => c.stopped === null);
}

/** "9:12": what is left of a link, from `now`; null once it expired. */
export function timeLeft(expiresAt: string, now: number): string | null {
  const ms = Date.parse(expiresAt) - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** How long a link is valid, in whole minutes, from when it was issued (or first seen) to when it expires. */
export function validMinutes(from: string | number, expiresAt: string): number {
  const start = typeof from === "number" ? from : Date.parse(from);
  return Math.max(1, Math.round((Date.parse(expiresAt) - start) / 60_000));
}

/** The re-read while a link is open: mutable so tests run it in milliseconds. */
export const telegramTiming = timing.telegram;
